import React from 'react';
import { Box, Typography, Chip, Paper, IconButton, Avatar } from '@mui/material';
import { Star, StarBorder, EmojiEvents, CalendarToday, People, Business } from '@mui/icons-material';
import { useFavoritesStore, useAuthStore } from '@/store';
import { useMeFavorites, useAddTournamentFavorite, useRemoveTournamentFavorite } from '@/hooks/useApi';
import type { Tournament, SportType } from '@/types';

interface TournamentCardProps {
  tournament: Tournament;
  compact?: boolean;
  onClick?: () => void;
}

const getSportIcon = (sportType: SportType): string => {
  const icons: Record<SportType, string> = {
    football: '⚽',
    hockey: '🏒',
    basketball: '🏀',
    tennis: '🎾',
    volleyball: '🏐',
    handball: '🤾',
    padel: '🎾',
    swimming: '🏊',
    poker: '🃏',
    other: '🏆',
  };
  return icons[sportType] || '🏆';
};

const getSportColor = (sportType: SportType): string => {
  const colors: Record<SportType, string> = {
    football: '#4caf50',
    hockey: '#2196f3',
    basketball: '#ff9800',
    tennis: '#e91e63',
    volleyball: '#9c27b0',
    handball: '#00bcd4',
    padel: '#8bc34a',
    swimming: '#03a9f4',
    poker: '#ff5722',
    other: '#757575',
  };
  return colors[sportType] || '#757575';
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ongoing':
      return '#4caf50';
    case 'published':
      return '#2196f3';
    case 'draft':
      return '#9e9e9e';
    case 'completed':
      return '#ff9800';
    case 'canceled':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

const getStatusText = (status: string): string => {
  switch (status) {
    case 'ongoing':
      return 'Идет';
    case 'published':
      return 'Опубликован';
    case 'draft':
      return 'Черновик';
    case 'completed':
      return 'Завершен';
    case 'canceled':
      return 'Отменен';
    case 'archived':
      return 'В архиве';
    default:
      return status;
  }
};

const formatDateRange = (startDate?: string | null, endDate?: string | null): string => {
  if (!startDate && !endDate) return 'Даты не указаны';
  const start = startDate ? new Date(startDate).toLocaleDateString('ru-RU') : '?';
  const end = endDate ? new Date(endDate).toLocaleDateString('ru-RU') : '?';
  return `${start} - ${end}`;
};

const TournamentLabels: React.FC<{ tournament: Tournament; height: number; fontSize: string }> = ({
  tournament,
  height,
  fontSize,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    <Chip
      label={getStatusText(tournament.status)}
      size="small"
      sx={{ height, fontSize, bgcolor: getStatusColor(tournament.status), color: 'white' }}
    />
    {tournament.cityName && (
      <Chip label={tournament.cityName} size="small" sx={{ height, fontSize, minWidth: 0 }} />
    )}
  </Box>
);

const headerTextSx = (height: number) =>
  ({
    flex: 1,
    minWidth: 0,
    height,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  }) as const;

export const TournamentCard: React.FC<TournamentCardProps> = ({
  tournament,
  compact = false,
  onClick,
}) => {
  const { isAuthenticated } = useAuthStore();
  const { data: apiFavorites } = useMeFavorites({ enabled: isAuthenticated });
  const addApi = useAddTournamentFavorite();
  const removeApi = useRemoveTournamentFavorite();
  const { isTournamentFavorite, addTournament, removeTournament } = useFavoritesStore();

  const isFavoriteFromApi = apiFavorites?.tournaments?.some((t) => (t as { id: string }).id === tournament.id);
  const isFavorite = isAuthenticated ? !!isFavoriteFromApi : isTournamentFavorite(tournament.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAuthenticated) {
      if (isFavorite) removeApi.mutate(tournament.id);
      else addApi.mutate(tournament.id);
    } else {
      if (isFavorite) removeTournament(tournament.id);
      else addTournament(tournament.id);
    }
  };

  if (compact) {
    return (
      <Paper
        onClick={onClick}
        sx={{
          p: 1.5,
          mb: 1,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: 'background.paper',
          '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: getSportColor(tournament.sportType),
            width: 40,
            height: 40,
            fontSize: '1.5rem',
          }}
        >
          {getSportIcon(tournament.sportType)}
        </Avatar>
        <Box sx={headerTextSx(40)}>
          <Typography variant="body2" fontWeight="medium" noWrap sx={{ lineHeight: '20px' }}>
            {tournament.name}
          </Typography>
          <TournamentLabels tournament={tournament} height={18} fontSize="0.65rem" />
        </Box>
        <IconButton
          size="small"
          onClick={handleFavoriteClick}
          sx={{ color: isFavorite ? 'warning.main' : 'text.secondary' }}
        >
          {isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
        </IconButton>
      </Paper>
    );
  }

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        mb: 1,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'background.paper',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: getSportColor(tournament.sportType),
            width: 48,
            height: 48,
            fontSize: '1.75rem',
          }}
        >
          {getSportIcon(tournament.sportType)}
        </Avatar>

        <Box sx={headerTextSx(48)}>
          <Typography variant="subtitle1" fontWeight="medium" noWrap sx={{ lineHeight: '26px' }}>
            {tournament.name}
          </Typography>
          <TournamentLabels tournament={tournament} height={20} fontSize="0.7rem" />
        </Box>

        <IconButton
          size="small"
          onClick={handleFavoriteClick}
          sx={{ color: isFavorite ? 'warning.main' : 'text.secondary' }}
        >
          {isFavorite ? <Star /> : <StarBorder />}
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, pl: 8, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarToday fontSize="small" color="action" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="text.secondary">
            {formatDateRange(tournament.startDate, tournament.endDate)}
          </Typography>
        </Box>
        {tournament.participants && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People fontSize="small" color="action" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              {tournament.participants.length} участников
            </Typography>
          </Box>
        )}
        {tournament.status === 'completed' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmojiEvents fontSize="small" color="warning" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="warning.main">
              Завершен
            </Typography>
          </Box>
        )}
        {!tournament.organizationId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Business fontSize="small" color="action" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              Без организации
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
